import { IonSpinner } from '@ionic/react'
import React, { FC, Fragment, RefObject, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AchievementsHook } from '../../hooks/useAchievements'
import AchievementCard from './AchievementCard'

interface AchievementListProps {
	achievementsByType: AchievementsHook['achievementsByType']
	setPointsAwarded: AchievementsHook['setPointsAwarded']
	setLastObservedStep: AchievementsHook['setLastObservedStep']
	isLoading: boolean
	pointsContainerRef: RefObject<HTMLDivElement>
}

const AchievementList: FC<AchievementListProps> = ({
	achievementsByType,
	setPointsAwarded,
	setLastObservedStep,
	isLoading,
	pointsContainerRef,
}) => {
	const { t } = useTranslation('pages_saving', { keyPrefix: 'goalsList' })

	const sortedAchievementsByType = useMemo(() => {
		if (!achievementsByType) return null

		const incompleteByType: Record<string, any[]> = {}
		const completedAchievements: any[] = []

		Object.entries(achievementsByType).forEach(([type, achievements]) => {
			achievements.forEach(achievement => {
				if (achievement.isCompleted) {
					completedAchievements.push(achievement)
				} else {
					if (!incompleteByType[type]) {
						incompleteByType[type] = []
					}
					incompleteByType[type].push(achievement)
				}
			})
		})

		return { incompleteByType, completedAchievements }
	}, [achievementsByType])

	return (
		<div className='flex flex-col gap-4 bg-white dark:bg-[#0d2d30] border border-text-grey-100 dark:border-surface-dark rounded-2xl p-4'>
			<div className='flex flex-col gap-2'>
				<span className='text-text-dark dark:text-white font-bold tracking-wide text-xl'>
					{t('title')}
				</span>
				<span className='text-sm text-text-grey-200 dark:text-[#dcdcdc]'>{t('description')}</span>
			</div>

			{isLoading && (
				<div className='flex items-center justify-center h-16'>
					<IonSpinner className='text-brand-primary-100-light dark:text-brand-primary-100-dark' />
				</div>
			)}

			{!isLoading && sortedAchievementsByType && (
				<>
					{Object.entries(sortedAchievementsByType.incompleteByType).map(
						([type, achievements], i) => (
							<Fragment key={type}>
								{i > 0 && <hr className='border-text-grey-100 dark:border-surface-dark' />}
								<div className='font-semibold mt-2 mb-1 uppercase text-[#127d27] dark:text-brand-primary-100-dark text-sm tracking-wider'>
									{t(type as any)}
								</div>
								{achievements.map((achievement, index) => {
									const pointsContainer = pointsContainerRef.current
									if (!pointsContainer) return null

									return (
										<Fragment
											key={achievement.id ?? `incomplete-${achievement.achievement.id}-${index}`}
										>
											{index > 0 && (
												<hr className='border-text-grey-100 dark:border-surface-dark' />
											)}
											<AchievementCard
												pointsContainer={pointsContainer}
												userAchievement={achievement}
												setPointsAwarded={setPointsAwarded}
												setLastObservedStep={setLastObservedStep}
											/>
										</Fragment>
									)
								})}
							</Fragment>
						)
					)}

					{sortedAchievementsByType.completedAchievements.length > 0 && (
						<>
							{Object.keys(sortedAchievementsByType.incompleteByType).length > 0 && (
								<hr className='border-text-grey-100 dark:border-surface-dark' />
							)}
							<div className='font-semibold mt-2 mb-1 uppercase text-[#127d27] dark:text-brand-primary-100-dark text-sm tracking-wider'>
								{t('complete')}
							</div>
							{sortedAchievementsByType.completedAchievements.map((achievement, index) => {
								const pointsContainer = pointsContainerRef.current
								if (!pointsContainer) return null

								return (
									<Fragment
										key={achievement.id ?? `completed-${achievement.achievement.id}-${index}`}
									>
										{index > 0 && <hr className='border-text-grey-100 dark:border-surface-dark	' />}
										<AchievementCard
											pointsContainer={pointsContainer}
											userAchievement={achievement}
											setPointsAwarded={setPointsAwarded}
											setLastObservedStep={setLastObservedStep}
										/>
									</Fragment>
								)
							})}
						</>
					)}
				</>
			)}
		</div>
	)
}

export default AchievementList
